import { Box, Link, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { CompactTable, type CompactTableColumn } from "@/components/common/CompactTable";
import { StatusChip } from "@/components/common/fields/StatusChipField";
import { getStatusColor } from "@/utils/helpers/getStatusColor";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { formatRelativeDate } from "@/utils/formatters/dateFormatter";
import { ColorType, UIComponentSize } from "@/types/shared/uiComponents";
import { ResourceName } from "@/types";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { ClaimWithContext } from "../hooks/useLandingPageData";

interface RecentClaimsTableProps {
  claims: ClaimWithContext[];
  title?: string;
  emptyMessage?: string;
}

const claimColumns: CompactTableColumn<ClaimWithContext>[] = [
  {
    key: "client",
    label: "Client",
    width: "1fr",
    render: (claim) =>
      claim.client_id_ref ? (
        <Link
          component={RouterLink}
          to={`/clients/${claim.client_id_ref}/show`}
          underline="always"
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          sx={{
            fontWeight: 500,
            fontSize: "0.875rem",
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {claim.clientName || "—"}
        </Link>
      ) : (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      ),
  },
  {
    key: "claimant",
    label: "Claimant",
    width: "1fr",
    render: (claim) =>
      claim.claimant_id_ref ? (
        <Link
          component={RouterLink}
          to={`/claimants/${claim.claimant_id_ref}/show`}
          underline="always"
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          sx={{
            fontWeight: 500,
            fontSize: "0.875rem",
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {claim.claimantName || "—"}
        </Link>
      ) : (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      ),
  },
  {
    key: "modified",
    label: "Modified",
    width: "80px",
    render: (claim) => (
      <Typography variant="caption" color="text.disabled">
        {formatRelativeDate(claim.modified_datetime)}
      </Typography>
    ),
  },
  {
    key: "cumulative_total",
    label: "Cumulative Total",
    width: "120px",
    align: "right",
    render: (claim) => (
      <Typography variant="body2" fontWeight={500}>
        {claim.final_cumulative_total ? formatUSD(claim.final_cumulative_total) : "—"}
      </Typography>
    ),
  },
  {
    key: "status",
    label: "Status",
    width: "140px",
    align: "right",
    render: (claim) => (
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <StatusChip
          label={formatToTitleCase(claim.claim_status)}
          color={getStatusColor(claim.claim_status) ?? ColorType.Default}
          size={UIComponentSize.Small}
        />
      </Box>
    ),
  },
];

export const RecentClaimsTable = ({
  claims,
  title = "Recent Claims",
  emptyMessage = "No claims to show",
}: RecentClaimsTableProps) => {
  const { canList } = useResourcePermissions(ResourceName.STOPLOSS_CLAIMS);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: "0.08em", color: "text.secondary" }}>
          {title}
        </Typography>
        {canList && (
          <Typography
            component={RouterLink}
            to="/stoploss-claims"
            variant="caption"
            sx={{
              color: "primary.main",
              textDecoration: "none",
              fontWeight: 500,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            View all <ArrowForwardIcon sx={{ fontSize: 12 }} />
          </Typography>
        )}
      </Stack>

      <CompactTable
        data={claims}
        columns={claimColumns}
        rowLink={(claim) => `/stoploss-claims/${claim.id}/show`}
        rowsPerPage={8}
        emptyMessage={emptyMessage}
      />
    </Box>
  );
};
